import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { ArrowLeft, BarChart3, Bot, FileText, Loader2, Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  adminAddChronology,
  adminAddConsultation,
  adminAddNote,
  adminAddParty,
  adminAddTask,
  adminGetCase,
  adminGetEvidenceUrl,
  adminRecordEvidence,
  adminSetTaskStatus,
  adminUpdateCase,
  adminUpdateEvidence,
} from "@/lib/api/admin-cases.functions";
import {
  DOC_LABELS,
  DOC_TYPES,
  adminGenerateMatterDocument,
  adminListMatterDocuments,
  adminMatterAiDraft,
  adminSaveReviewedAiDraft,
  type MatterDocType,
} from "@/lib/api/admin-case-docs.functions";

export const Route = createFileRoute("/_authenticated/portal/admin/cases/$id")({
  head: () => ({
    meta: [
      { title: "Matter workspace · NDLCS Admin" },
      { name: "description", content: "Manage a single client matter end to end." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CaseWorkspace,
});

const TABS = [
  "Overview",
  "Parties",
  "Consultations",
  "Evidence",
  "Chronology",
  "Tasks",
  "Documents",
  "AI assistant",
  "Notes",
  "Activity",
] as const;

const inputCls = "w-full rounded-md border border-input bg-background px-3 py-2 text-sm";
const btnCls =
  "inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-2 text-sm font-medium text-white hover:bg-navy/90 disabled:opacity-60";

function downloadBase64Pdf(base64: string, filename: string) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}


function CaseWorkspace() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const getCase = useServerFn(adminGetCase);
  const [tab, setTab] = useState<(typeof TABS)[number]>("Overview");

  const q = useQuery({
    queryKey: ["admin-case", id],
    queryFn: () => getCase({ data: { id } }),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-case", id] });

  if (q.isPending) {
    return (
      <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading matter…
      </p>
    );
  }
  if (q.error || !q.data) {
    return <p className="py-6 text-sm text-red-700">{String((q.error as any)?.message)}</p>;
  }

  const d = q.data as any;
  const m = d.matter;

  return (
    <div className="space-y-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/portal/admin/cases"
          className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-navy"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> All matters
        </Link>
        <Link
          to="/portal/admin/cases/reports"
          className="inline-flex items-center gap-1.5 rounded-md border border-input px-3 py-1.5 text-xs font-medium hover:bg-muted"
        >
          <BarChart3 className="h-3.5 w-3.5" /> Reports
        </Link>
      </div>

      <header className="rounded-lg border border-border bg-card p-4">
        <p className="text-xs font-semibold uppercase tracking-widest text-gold">
          {m.matter_number}
        </p>
        <h1 className="font-display text-2xl text-navy">{m.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {m.client_name || m.client_email} · {m.matter_type || m.category} ·{" "}
          {m.department || "Legal"} · opened {m.date_opened}
        </p>
      </header>

      <nav className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`rounded-t-md px-3 py-2 text-sm font-medium ${
              tab === t ? "bg-card text-navy shadow-sm" : "text-muted-foreground hover:text-navy"
            }`}
          >
            {t}
          </button>
        ))}
      </nav>

      {tab === "Overview" && <OverviewTab matter={m} onSaved={refresh} />}
      {tab === "Parties" && <PartiesTab id={id} parties={d.parties} onSaved={refresh} />}
      {tab === "Consultations" && (
        <ConsultationsTab id={id} rows={d.consultations} onSaved={refresh} />
      )}
      {tab === "Evidence" && <EvidenceTab id={id} rows={d.evidence} onSaved={refresh} />}
      {tab === "Chronology" && (
        <ChronologyTab id={id} rows={d.chronology} evidence={d.evidence} onSaved={refresh} />
      )}
      {tab === "Tasks" && <TasksTab id={id} rows={d.tasks} onSaved={refresh} />}
      {tab === "Documents" && <DocumentsTab id={id} matter={m} />}
      {tab === "AI assistant" && <AiAssistantTab id={id} onSaved={refresh} />}
      {tab === "Notes" && <NotesTab id={id} rows={d.notes} onSaved={refresh} />}
      {tab === "Activity" && <ActivityTab rows={d.activity} />}

    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-4">
      <h2 className="text-sm font-semibold text-navy">{title}</h2>
      {children}
    </section>
  );
}

function OverviewTab({ matter, onSaved }: { matter: any; onSaved: () => void }) {
  const update = useServerFn(adminUpdateCase);
  const [form, setForm] = useState({
    status: matter.status ?? "open",
    priority: matter.priority ?? "normal",
    assigned_to: matter.assigned_to ?? "",
    due_date: matter.due_date ?? "",
    notes: matter.notes ?? "",
  });
  const save = useMutation({
    mutationFn: () => update({ data: { id: matter.id, ...form } as any }),
    onSuccess: onSaved,
  });

  return (
    <Card title="Matter status & instructions">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="ov-status" className="text-xs font-medium text-muted-foreground">
            Status
          </label>
          <select
            id="ov-status"
            className={`mt-1 ${inputCls}`}
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
          >
            <option value="open">Open</option>
            <option value="in_review">In review</option>
            <option value="in_progress">In progress</option>
            <option value="awaiting_client">Awaiting client</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div>
          <label htmlFor="ov-priority" className="text-xs font-medium text-muted-foreground">
            Priority
          </label>
          <select
            id="ov-priority"
            className={`mt-1 ${inputCls}`}
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
          >
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
        <div>
          <label htmlFor="ov-assigned" className="text-xs font-medium text-muted-foreground">
            Assigned to
          </label>
          <input
            id="ov-assigned"
            className={`mt-1 ${inputCls}`}
            value={form.assigned_to}
            onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
          />
        </div>
        <div>
          <label htmlFor="ov-due" className="text-xs font-medium text-muted-foreground">
            Key due date
          </label>
          <input
            id="ov-due"
            type="date"
            className={`mt-1 ${inputCls}`}
            value={form.due_date ?? ""}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
          />
        </div>
      </div>
      <div>
        <label htmlFor="ov-notes" className="text-xs font-medium text-muted-foreground">
          Instructions / summary
        </label>
        <textarea
          id="ov-notes"
          rows={5}
          className={`mt-1 ${inputCls}`}
          value={form.notes ?? ""}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </div>
      {matter.date_closed ? (
        <p className="text-xs text-muted-foreground">
          Closed {matter.date_closed} · retained until {matter.retain_until ?? "—"} (POPIA 5-year
          retention).
        </p>
      ) : null}
      <button
        type="button"
        className={btnCls}
        disabled={save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save changes
      </button>
    </Card>
  );
}

function PartiesTab({ id, parties, onSaved }: { id: string; parties: any[]; onSaved: () => void }) {
  const add = useServerFn(adminAddParty);
  const [form, setForm] = useState({
    role: "witness",
    full_name: "",
    email: "",
    telephone: "",
    company_name: "",
    relationship: "",
  });
  const save = useMutation({
    mutationFn: () => add({ data: { matter_id: id, ...form } as any }),
    onSuccess: () => {
      setForm({ ...form, full_name: "", email: "", telephone: "", relationship: "" });
      onSaved();
    },
  });

  return (
    <div className="space-y-4">
      <Card title="Parties on record">
        {parties.length === 0 ? (
          <p className="text-sm text-muted-foreground">No parties captured.</p>
        ) : (
          <ul className="divide-y divide-border text-sm">
            {parties.map((p) => (
              <li key={p.id} className="py-2">
                <p className="font-medium text-navy">
                  {p.full_name}{" "}
                  <span className="text-xs uppercase text-muted-foreground">({p.role})</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {[p.email, p.telephone, p.company_name, p.relationship]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title="Add a party">
        <div className="grid gap-3 sm:grid-cols-2">
          <select
            aria-label="Party role"
            className={inputCls}
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
          >
            <option value="witness">Witness</option>
            <option value="opposing">Opposing party</option>
            <option value="representative">Representative</option>
            <option value="third_party">Third party</option>
            <option value="other">Other</option>
          </select>
          <input
            aria-label="Full name"
            placeholder="Full name"
            className={inputCls}
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
          />
          <input
            aria-label="Email"
            placeholder="Email"
            className={inputCls}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <input
            aria-label="Telephone"
            placeholder="Telephone"
            className={inputCls}
            value={form.telephone}
            onChange={(e) => setForm({ ...form, telephone: e.target.value })}
          />
          <input
            aria-label="Company"
            placeholder="Company"
            className={inputCls}
            value={form.company_name}
            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
          />
          <input
            aria-label="Relationship to matter"
            placeholder="Relationship to matter"
            className={inputCls}
            value={form.relationship}
            onChange={(e) => setForm({ ...form, relationship: e.target.value })}
          />
        </div>
        <button
          type="button"
          className={btnCls}
          disabled={save.isPending || form.full_name.trim().length < 2}
          onClick={() => save.mutate()}
        >
          Add party
        </button>
      </Card>
    </div>
  );
}

function ConsultationsTab({
  id,
  rows,
  onSaved,
}: {
  id: string;
  rows: any[];
  onSaved: () => void;
}) {
  const add = useServerFn(adminAddConsultation);
  const [form, setForm] = useState({
    consulted_at: "",
    duration_minutes: 30,
    staff_member: "",
    advice_given: "",
    client_instructions: "",
    next_steps: "",
  });
  const save = useMutation({
    mutationFn: () =>
      add({
        data: {
          matter_id: id,
          ...form,
          consulted_at: form.consulted_at ? new Date(form.consulted_at).toISOString() : "",
        } as any,
      }),
    onSuccess: () => {
      setForm({ ...form, advice_given: "", client_instructions: "", next_steps: "" });
      onSaved();
    },
  });

  return (
    <div className="space-y-4">
      <Card title="Consultation record">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No consultations logged.</p>
        ) : (
          <ul className="space-y-3 text-sm">
            {rows.map((c) => (
              <li key={c.id} className="rounded-md border border-border p-3">
                <p className="text-xs text-muted-foreground">
                  {new Date(c.consulted_at).toLocaleString()} · {c.duration_minutes} min ·{" "}
                  {c.staff_member || "—"}
                </p>
                {c.advice_given ? (
                  <p className="mt-1">
                    <strong>Advice:</strong> {c.advice_given}
                  </p>
                ) : null}
                {c.client_instructions ? (
                  <p className="mt-1">
                    <strong>Instructions:</strong> {c.client_instructions}
                  </p>
                ) : null}
                {c.next_steps ? (
                  <p className="mt-1">
                    <strong>Next steps:</strong> {c.next_steps}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title="Log a consultation">
        <div className="grid gap-3 sm:grid-cols-3">
          <input
            aria-label="Consultation date and time"
            type="datetime-local"
            className={inputCls}
            value={form.consulted_at}
            onChange={(e) => setForm({ ...form, consulted_at: e.target.value })}
          />
          <input
            aria-label="Duration in minutes"
            type="number"
            min={0}
            className={inputCls}
            value={form.duration_minutes}
            onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })}
          />
          <input
            aria-label="Staff member"
            placeholder="Staff member"
            className={inputCls}
            value={form.staff_member}
            onChange={(e) => setForm({ ...form, staff_member: e.target.value })}
          />
        </div>
        <textarea
          aria-label="Advice given"
          placeholder="Advice given (non-reserved matters)"
          rows={3}
          className={inputCls}
          value={form.advice_given}
          onChange={(e) => setForm({ ...form, advice_given: e.target.value })}
        />
        <textarea
          aria-label="Client instructions"
          placeholder="Client instructions"
          rows={3}
          className={inputCls}
          value={form.client_instructions}
          onChange={(e) => setForm({ ...form, client_instructions: e.target.value })}
        />
        <textarea
          aria-label="Next steps"
          placeholder="Next steps"
          rows={2}
          className={inputCls}
          value={form.next_steps}
          onChange={(e) => setForm({ ...form, next_steps: e.target.value })}
        />
        <button type="button" className={btnCls} disabled={save.isPending} onClick={() => save.mutate()}>
          Save consultation
        </button>
      </Card>
    </div>
  );
}

function EvidenceTab({ id, rows, onSaved }: { id: string; rows: any[]; onSaved: () => void }) {
  const record = useServerFn(adminRecordEvidence);
  const signUrl = useServerFn(adminGetEvidenceUrl);
  const updateEvidence = useServerFn(adminUpdateEvidence);
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [editTags, setEditTags] = useState("");
  const [editAnnexure, setEditAnnexure] = useState("");
  const [preview, setPreview] = useState<{ url: string; name: string; pdf: boolean } | null>(null);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const path = `${id}/${crypto.randomUUID()}-${file.name.replace(/[^\w.\-]/g, "_")}`;
      const { error: upErr } = await supabase.storage
        .from("matter-evidence")
        .upload(path, file, { upsert: false });
      if (upErr) throw upErr;
      await record({
        data: {
          matter_id: id,
          label: label || file.name,
          description,
          storage_path: path,
          original_filename: file.name,
          mime_type: file.type,
          size_bytes: file.size,
          tags: tags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
            .slice(0, 20),
        } as any,
      });
      setLabel("");
      setDescription("");
      setTags("");
      setFile(null);
      onSaved();
    } catch (e: any) {
      setError(e?.message ?? "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const open = useMutation({
    mutationFn: (row: any) =>
      signUrl({ data: { id: row.id, matter_id: id } }).then((r: any) => ({
        url: r.url as string,
        name: row.original_filename as string,
        pdf: /pdf/i.test(row.mime_type ?? "") || /\.pdf$/i.test(row.original_filename ?? ""),
      })),
    onSuccess: (res) => setPreview(res),
  });

  const saveMeta = useMutation({
    mutationFn: (evidenceId: string) =>
      updateEvidence({
        data: {
          id: evidenceId,
          matter_id: id,
          annexure: editAnnexure || undefined,
          tags: editTags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean),
        } as any,
      }),
    onSuccess: () => {
      setEditing(null);
      onSaved();
    },
  });

  // Group by label so re-uploads read as a version history rather than dupes.
  const groups = new Map<string, any[]>();
  for (const e of rows) {
    const key = String(e.label);
    groups.set(key, [...(groups.get(key) ?? []), e]);
  }
  for (const list of groups.values())
    list.sort((a, b) => (b.version ?? 1) - (a.version ?? 1));

  return (
    <div className="space-y-4">
      <Card title="Evidence bundle">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No evidence uploaded.</p>
        ) : (
          <ul className="divide-y divide-border text-sm">
            {Array.from(groups.entries()).map(([key, versions]) => {
              const e = versions[0];
              return (
                <li key={key} className="space-y-2 py-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-navy">
                        Annexure {e.annexure ?? "—"} — {e.label}
                        {versions.length > 1 ? (
                          <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                            v{e.version} · {versions.length} versions
                          </span>
                        ) : null}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {e.original_filename} · {(e.size_bytes / 1024).toFixed(0)} KB · retained
                        until {e.retain_until ?? "—"}
                        {e.ocr_text ? " · text extracted" : ""}
                      </p>
                      {e.tags?.length ? (
                        <p className="mt-1 flex flex-wrap gap-1">
                          {e.tags.map((t: string) => (
                            <span
                              key={t}
                              className="rounded-full bg-navy/5 px-2 py-0.5 text-[11px] text-navy"
                            >
                              {t}
                            </span>
                          ))}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="rounded-md border border-input px-2 py-1 text-xs font-medium hover:bg-muted"
                        onClick={() => open.mutate(e)}
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        className="rounded-md border border-input px-2 py-1 text-xs font-medium hover:bg-muted"
                        onClick={() => {
                          setEditing(editing === e.id ? null : e.id);
                          setEditTags((e.tags ?? []).join(", "));
                          setEditAnnexure(e.annexure ?? "");
                        }}
                      >
                        Tag
                      </button>
                    </div>
                  </div>

                  {editing === e.id ? (
                    <div className="flex flex-wrap items-end gap-2 rounded-md bg-muted/40 p-3">
                      <div>
                        <label
                          htmlFor={`annex-${e.id}`}
                          className="text-xs text-muted-foreground"
                        >
                          Annexure
                        </label>
                        <input
                          id={`annex-${e.id}`}
                          className="mt-1 w-20 rounded-md border border-input bg-background px-2 py-1 text-sm"
                          value={editAnnexure}
                          onChange={(ev) => setEditAnnexure(ev.target.value)}
                        />
                      </div>
                      <div className="flex-1 min-w-[200px]">
                        <label htmlFor={`tags-${e.id}`} className="text-xs text-muted-foreground">
                          Tags (comma separated)
                        </label>
                        <input
                          id={`tags-${e.id}`}
                          className="mt-1 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                          value={editTags}
                          onChange={(ev) => setEditTags(ev.target.value)}
                        />
                      </div>
                      <button
                        type="button"
                        className={btnCls}
                        disabled={saveMeta.isPending}
                        onClick={() => saveMeta.mutate(e.id)}
                      >
                        Save
                      </button>
                    </div>
                  ) : null}

                  {versions.length > 1 ? (
                    <ul className="ml-3 space-y-1 border-l border-border pl-3 text-xs text-muted-foreground">
                      {versions.slice(1).map((v) => (
                        <li key={v.id} className="flex items-center justify-between gap-2">
                          <span>
                            v{v.version} · {v.original_filename} ·{" "}
                            {String(v.created_at ?? "").slice(0, 10)}
                          </span>
                          <button
                            type="button"
                            className="underline hover:text-navy"
                            onClick={() => open.mutate(v)}
                          >
                            Preview
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {preview ? (
        <Card title={`Preview — ${preview.name}`}>
          {preview.pdf ? (
            <iframe title={preview.name} src={preview.url} className="h-[600px] w-full rounded-md border border-border" />
          ) : /\.(png|jpe?g|webp|gif)$/i.test(preview.name) ? (
            <img src={preview.url} alt={preview.name} className="max-h-[600px] rounded-md" />
          ) : (
            <a href={preview.url} target="_blank" rel="noopener" className="text-sm underline">
              Open file in a new tab
            </a>
          )}
          <button
            type="button"
            className="rounded-md border border-input px-2 py-1 text-xs font-medium hover:bg-muted"
            onClick={() => setPreview(null)}
          >
            Close preview
          </button>
        </Card>
      ) : null}

      <Card title="Upload evidence">
        <p className="text-xs text-muted-foreground">
          Files are stored in encrypted private storage, retained for 5 years and every view is
          logged. Re-upload with the same label to create a new version.
        </p>
        <input
          aria-label="Evidence label"
          placeholder="Label (e.g. Dismissal letter)"
          className={inputCls}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <textarea
          aria-label="Evidence description"
          placeholder="Description"
          rows={2}
          className={inputCls}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <input
          aria-label="Evidence tags"
          placeholder="Tags, comma separated (e.g. contract, payslip)"
          className={inputCls}
          value={tags}
          onChange={(e) => setTags(e.target.value)}
        />
        <input
          aria-label="Evidence file"
          type="file"
          className="text-sm"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        {error ? <p className="text-sm text-red-700">{error}</p> : null}
        <button type="button" className={btnCls} disabled={!file || busy} onClick={upload}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{" "}
          Upload
        </button>
      </Card>
    </div>
  );
}

function DocumentsTab({ id, matter }: { id: string; matter: any }) {
  const generate = useServerFn(adminGenerateMatterDocument);
  const list = useServerFn(adminListMatterDocuments);
  const [docType, setDocType] = useState<MatterDocType>("letter");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const docs = useQuery({
    queryKey: ["matter-docs", id],
    queryFn: () => list({ data: { matter_id: id } }),
  });

  const make = useMutation({
    mutationFn: () =>
      generate({
        data: {
          matter_id: id,
          doc_type: docType,
          title: title || DOC_LABELS[docType],
          body,
          save: true,
        } as any,
      }),
    onSuccess: (res: any) => {
      downloadBase64Pdf(res.base64, res.filename);
      docs.refetch();
    },
  });

  return (
    <div className="space-y-4">
      <Card title="Generate a branded document">
        <p className="text-xs text-muted-foreground">
          Matter fields ({matter.matter_number}, client, parties, handler) are inserted
          automatically. Use blank lines for paragraphs, “#” for headings and “-” for bullets.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="doc-type" className="text-xs text-muted-foreground">
              Document type
            </label>
            <select
              id="doc-type"
              className={inputCls}
              value={docType}
              onChange={(e) => setDocType(e.target.value as MatterDocType)}
            >
              {DOC_TYPES.map((t) => (
                <option key={t} value={t}>
                  {DOC_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="doc-title" className="text-xs text-muted-foreground">
              Title
            </label>
            <input
              id="doc-title"
              className={inputCls}
              value={title}
              placeholder={DOC_LABELS[docType]}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
        </div>
        <textarea
          aria-label="Document body"
          rows={12}
          className={inputCls}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={"# Introduction\nWe act on behalf of our client…"}
        />
        {make.error ? (
          <p className="text-sm text-red-700">{String((make.error as any).message)}</p>
        ) : null}
        <button
          type="button"
          className={btnCls}
          disabled={!body.trim() || make.isPending}
          onClick={() => make.mutate()}
        >
          {make.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileText className="h-4 w-4" />
          )}{" "}
          Generate PDF
        </button>
      </Card>

      <Card title="Documents on this matter">
        {docs.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (docs.data as any)?.documents?.length ? (
          <ul className="divide-y divide-border text-sm">
            {(docs.data as any).documents.map((doc: any) => (
              <li key={doc.id} className="py-2">
                <p className="font-medium text-navy">
                  {doc.title}{" "}
                  <span className="text-xs font-normal text-muted-foreground">
                    v{doc.version}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {DOC_LABELS[doc.doc_type as MatterDocType] ?? doc.doc_type} · {doc.status} ·{" "}
                  {doc.ai_generated ? "AI drafted (reviewed)" : "Manually drafted"} ·{" "}
                  {String(doc.created_at).slice(0, 10)}
                </p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No documents yet.</p>
        )}
      </Card>
    </div>
  );
}

function AiAssistantTab({ id, onSaved }: { id: string; onSaved: () => void }) {
  const draft = useServerFn(adminMatterAiDraft);
  const saveDraft = useServerFn(adminSaveReviewedAiDraft);
  const addTask = useServerFn(adminAddTask);
  const [mode, setMode] = useState<"analysis" | "letter" | "tasks">("analysis");
  const [instruction, setInstruction] = useState("");
  const [text, setText] = useState("");
  const [tasks, setTasks] = useState<{ owner: string; dueInDays: number; description: string }[]>(
    [],
  );
  const [title, setTitle] = useState("");
  const [reviewed, setReviewed] = useState(false);

  const run = useMutation({
    mutationFn: () => draft({ data: { matter_id: id, mode, instruction } as any }),
    onSuccess: (res: any) => {
      setText(res.draft);
      setTasks(res.tasks ?? []);
      setReviewed(false);
    },
  });

  const save = useMutation({
    mutationFn: () =>
      saveDraft({
        data: {
          matter_id: id,
          title: title || (mode === "letter" ? "AI letter draft" : "AI legal analysis"),
          doc_type: mode === "letter" ? "letter" : "opinion",
          body: text,
          reviewed: true,
        } as any,
      }),
    onSuccess: () => {
      setText("");
      setReviewed(false);
      onSaved();
    },
  });

  const commitTasks = useMutation({
    mutationFn: async () => {
      for (const t of tasks) {
        const due = new Date();
        due.setDate(due.getDate() + (t.dueInDays || 7));
        await addTask({
          data: {
            matter_id: id,
            title: t.description.slice(0, 200),
            owner: t.owner || "",
            due_date: due.toISOString().slice(0, 10),
          } as any,
        });
      }
    },
    onSuccess: () => {
      setTasks([]);
      onSaved();
    },
  });

  return (
    <div className="space-y-4">
      <Card title="AI Matter Assistant">
        <p className="text-xs text-muted-foreground">
          The assistant drafts from this matter’s recorded facts only. Every output is a draft —
          nothing is saved to the file until you confirm you have reviewed it.
        </p>
        <div className="flex flex-wrap gap-2">
          {(["analysis", "letter", "tasks"] as const).map((mmode) => (
            <button
              key={mmode}
              type="button"
              onClick={() => setMode(mmode)}
              className={`rounded-md border px-3 py-1.5 text-xs font-medium ${
                mode === mmode
                  ? "border-navy bg-navy text-white"
                  : "border-input hover:bg-muted"
              }`}
            >
              {mmode === "analysis"
                ? "Legal analysis"
                : mmode === "letter"
                  ? "Draft letter"
                  : "Task list"}
            </button>
          ))}
        </div>
        <textarea
          aria-label="Instruction to the assistant"
          rows={3}
          className={inputCls}
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder="Optional instruction, e.g. focus on the CCMA time limits and condonation."
        />
        {run.error ? (
          <p className="text-sm text-red-700">{String((run.error as any).message)}</p>
        ) : null}
        <button type="button" className={btnCls} disabled={run.isPending} onClick={() => run.mutate()}>
          {run.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bot className="h-4 w-4" />}{" "}
          Generate draft
        </button>
      </Card>

      {text ? (
        <Card title="Draft — manual review required">
          <textarea
            aria-label="AI draft"
            rows={18}
            className={inputCls}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setReviewed(false);
            }}
          />
          {mode !== "tasks" ? (
            <>
              <input
                aria-label="Document title"
                className={inputCls}
                value={title}
                placeholder="Document title"
                onChange={(e) => setTitle(e.target.value)}
              />
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={reviewed}
                  onChange={(e) => setReviewed(e.target.checked)}
                />
                <span>
                  I have read this draft in full, verified the facts and authorities, and approve it
                  for the matter file.
                </span>
              </label>
              <button
                type="button"
                className={btnCls}
                disabled={!reviewed || save.isPending}
                onClick={() => save.mutate()}
              >
                Save reviewed draft
              </button>
            </>
          ) : null}
        </Card>
      ) : null}

      {tasks.length ? (
        <Card title="Suggested tasks — review before adding">
          <ul className="space-y-1 text-sm">
            {tasks.map((t, i) => (
              <li key={i}>
                <strong className="text-navy">{t.description}</strong>{" "}
                <span className="text-xs text-muted-foreground">
                  ({t.owner || "unassigned"} · due in {t.dueInDays} days)
                </span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className={btnCls}
            disabled={commitTasks.isPending}
            onClick={() => commitTasks.mutate()}
          >
            Add {tasks.length} tasks to the matter
          </button>
        </Card>
      ) : null}
    </div>
  );
}


function ChronologyTab({
  id,
  rows,
  evidence,
  onSaved,
}: {
  id: string;
  rows: any[];
  evidence: any[];
  onSaved: () => void;
}) {
  const add = useServerFn(adminAddChronology);
  const [form, setForm] = useState({
    event_date: new Date().toISOString().slice(0, 10),
    event: "",
    evidence_id: "",
  });
  const save = useMutation({
    mutationFn: () =>
      add({
        data: {
          matter_id: id,
          event_date: form.event_date,
          event: form.event,
          ...(form.evidence_id ? { evidence_id: form.evidence_id } : {}),
        } as any,
      }),
    onSuccess: () => {
      setForm({ ...form, event: "", evidence_id: "" });
      onSaved();
    },
  });

  return (
    <div className="space-y-4">
      <Card title="Chronology of events">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No events recorded.</p>
        ) : (
          <ol className="space-y-2 text-sm">
            {rows.map((c) => (
              <li key={c.id} className="border-l-2 border-gold pl-3">
                <p className="text-xs text-muted-foreground">{c.event_date}</p>
                <p>{c.event}</p>
              </li>
            ))}
          </ol>
        )}
      </Card>
      <Card title="Add an event">
        <div className="grid gap-3 sm:grid-cols-3">
          <input
            aria-label="Event date"
            type="date"
            className={inputCls}
            value={form.event_date}
            onChange={(e) => setForm({ ...form, event_date: e.target.value })}
          />
          <input
            aria-label="Event description"
            placeholder="What happened"
            className={`sm:col-span-2 ${inputCls}`}
            value={form.event}
            onChange={(e) => setForm({ ...form, event: e.target.value })}
          />
        </div>
        <select
          aria-label="Link evidence"
          className={inputCls}
          value={form.evidence_id}
          onChange={(e) => setForm({ ...form, evidence_id: e.target.value })}
        >
          <option value="">No linked evidence</option>
          {evidence.map((e: any) => (
            <option key={e.id} value={e.id}>
              Annexure {e.annexure} — {e.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className={btnCls}
          disabled={save.isPending || form.event.trim().length < 2}
          onClick={() => save.mutate()}
        >
          Add event
        </button>
      </Card>
    </div>
  );
}

function TasksTab({ id, rows, onSaved }: { id: string; rows: any[]; onSaved: () => void }) {
  const add = useServerFn(adminAddTask);
  const setStatus = useServerFn(adminSetTaskStatus);
  const [form, setForm] = useState({ title: "", owner: "", due_date: "", detail: "" });
  const save = useMutation({
    mutationFn: () => add({ data: { matter_id: id, ...form } as any }),
    onSuccess: () => {
      setForm({ title: "", owner: "", due_date: "", detail: "" });
      onSaved();
    },
  });
  const flip = useMutation({
    mutationFn: (v: { id: string; status: "open" | "in_progress" | "done" }) =>
      setStatus({ data: { id: v.id, matter_id: id, status: v.status } }),
    onSuccess: onSaved,
  });

  return (
    <div className="space-y-4">
      <Card title="Tasks & deadlines">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tasks yet.</p>
        ) : (
          <ul className="divide-y divide-border text-sm">
            {rows.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-3 py-2">
                <div>
                  <p className={t.status === "done" ? "line-through text-muted-foreground" : ""}>
                    {t.title}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {[t.owner, t.due_date ? `due ${t.due_date}` : null, t.status]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
                <select
                  aria-label={`Status for ${t.title}`}
                  className="rounded-md border border-input bg-background px-2 py-1 text-xs"
                  value={t.status}
                  onChange={(e) => flip.mutate({ id: t.id, status: e.target.value as any })}
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In progress</option>
                  <option value="done">Done</option>
                </select>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title="Add a task">
        <div className="grid gap-3 sm:grid-cols-3">
          <input
            aria-label="Task title"
            placeholder="Task"
            className={inputCls}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <input
            aria-label="Task owner"
            placeholder="Owner"
            className={inputCls}
            value={form.owner}
            onChange={(e) => setForm({ ...form, owner: e.target.value })}
          />
          <input
            aria-label="Task due date"
            type="date"
            className={inputCls}
            value={form.due_date}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
          />
        </div>
        <textarea
          aria-label="Task detail"
          placeholder="Detail"
          rows={2}
          className={inputCls}
          value={form.detail}
          onChange={(e) => setForm({ ...form, detail: e.target.value })}
        />
        <button
          type="button"
          className={btnCls}
          disabled={save.isPending || form.title.trim().length < 2}
          onClick={() => save.mutate()}
        >
          Add task
        </button>
      </Card>
    </div>
  );
}

function NotesTab({ id, rows, onSaved }: { id: string; rows: any[]; onSaved: () => void }) {
  const add = useServerFn(adminAddNote);
  const [body, setBody] = useState("");
  const save = useMutation({
    mutationFn: () => add({ data: { matter_id: id, body } as any }),
    onSuccess: () => {
      setBody("");
      onSaved();
    },
  });

  return (
    <div className="space-y-4">
      <Card title="Internal notes">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No notes.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {rows.map((n) => (
              <li key={n.id} className="rounded-md border border-border p-3">
                <p className="text-xs text-muted-foreground">
                  {new Date(n.created_at).toLocaleString()}
                </p>
                <p className="mt-1 whitespace-pre-wrap">{n.body}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title="Add a note">
        <textarea
          aria-label="Internal note"
          rows={4}
          className={inputCls}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <button
          type="button"
          className={btnCls}
          disabled={save.isPending || body.trim().length < 2}
          onClick={() => save.mutate()}
        >
          Save note
        </button>
      </Card>
    </div>
  );
}

function ActivityTab({ rows }: { rows: any[] }) {
  return (
    <Card title="Audit trail">
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No activity yet.</p>
      ) : (
        <ul className="divide-y divide-border text-sm">
          {rows.map((a) => (
            <li key={a.id} className="py-2">
              <p className="font-medium text-navy">{a.action.replace(/_/g, " ")}</p>
              <p className="text-xs text-muted-foreground">
                {new Date(a.created_at).toLocaleString()}
                {a.detail ? ` · ${a.detail}` : ""}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
